import React, { useState, useEffect } from 'react';
import { useLang } from '../../contexts/LangContext';
import { generateBillingPeriods } from '../../utils/billingPeriods';
import { buildingsApi } from '../../api/buildingsApi';
import { apartmentsApi } from '../../api/apartmentsApi';
import { parseContractAmountField } from '../../utils/contractAmount.util';
import { formatVndInput } from '../../utils/vndFormat';

interface AddOtherServiceFeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  apartmentId?: string;
  onSave: (data: any) => Promise<void>;
}

const AddOtherServiceFeeModal: React.FC<AddOtherServiceFeeModalProps> = ({
  isOpen,
  onClose,
  apartmentId,
  onSave,
}) => {
  // const { t } = useLang(); // Removed unused variable
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form data
  const [billingPeriod, setBillingPeriod] = useState(() => {
    const periods = generateBillingPeriods();
    return periods[0]?.value || '';
  });
  const [amountInput, setAmountInput] = useState('');
  const [notes, setNotes] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [applyToAllApartments, setApplyToAllApartments] = useState(false);
  
  // Building settings
  const [buildingSettings, setBuildingSettings] = useState<any>(null);
  
  const billingPeriods = generateBillingPeriods();

  // Load building settings when modal opens
  useEffect(() => {
    if (isOpen && apartmentId) {
      loadBuildingSettings();
    }
  }, [isOpen, apartmentId]);

  // Update date ranges when billing period or building settings change
  useEffect(() => {
    if (buildingSettings && billingPeriod) {
      calculateDateRanges();
    }
  }, [buildingSettings, billingPeriod]);

  const loadBuildingSettings = async () => {
    if (!apartmentId) return;
    
    try {
      console.log('Loading building settings for apartment:', apartmentId);
      const apartment = await apartmentsApi.getApartmentById(apartmentId);
      console.log('Apartment data:', apartment);
      const building = await buildingsApi.getBuilding(apartment.buildingId);
      console.log('Building settings loaded:', building);
      setBuildingSettings(building);
    } catch (err) {
      console.error('Failed to load building settings:', err);
    }
  };

  const calculateDateRanges = () => {
    console.log('Calculating date ranges with:', { buildingSettings, billingPeriod });
    
    if (!buildingSettings || !billingPeriod) {
      console.log('Missing data for date calculation');
      return;
    }
    
    try {
      // Handle both formats: "06/2025" and "Tháng 06/2025"
      const periodMatch = billingPeriod.match(/(\d{2})\/(\d{4})/);
      if (!periodMatch) {
        console.error('Invalid billing period format:', billingPeriod);
        return;
      }
      
      const [, month, year] = periodMatch;
      const monthNum = parseInt(month);
      const yearNum = parseInt(year);
      
      console.log('Billing period parsed:', { month, year, monthNum, yearNum });
      
      if (isNaN(monthNum) || isNaN(yearNum) || monthNum < 1 || monthNum > 12) {
        console.error('Invalid billing period format:', billingPeriod);
        return;
      }
      
            // From date: paymentFromDay of current month - using UTC to avoid timezone issues
      const fromDay = Math.max(1, Math.min(31, buildingSettings.paymentFromDay || 10));
      const fromDateObj = new Date(Date.UTC(yearNum, monthNum - 1, fromDay));
      
      // Due date: paymentToDay of current month - using UTC to avoid timezone issues
      const toDay = Math.max(1, Math.min(31, buildingSettings.paymentToDay || 20));
      const dueDateObj = new Date(Date.UTC(yearNum, monthNum - 1, toDay));
      
      console.log('Date calculation:', {
        fromDay,
        toDay,
        fromDateObj,
        dueDateObj,
        buildingSettings: {
          paymentFromDay: buildingSettings.paymentFromDay,
          paymentToDay: buildingSettings.paymentToDay
        }
      });
      
      // Validate dates before converting to ISO string
      if (isNaN(fromDateObj.getTime()) || isNaN(dueDateObj.getTime())) {
        console.error('Invalid date created from building settings:', { fromDay, toDay, monthNum, yearNum });
        return;
      }
      
      const fromDateString = fromDateObj.toISOString().split('T')[0];
      const dueDateString = dueDateObj.toISOString().split('T')[0];
      
      console.log('Setting dates:', { fromDateString, dueDateString });
      
      setFromDate(fromDateString);
      setDueDate(dueDateString);
    } catch (error) {
      console.error('Error calculating date ranges:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const data = {
        apartmentId: applyToAllApartments ? null : apartmentId,
        billingPeriod,
        amount: parseContractAmountField(amountInput),
        notes,
        fromDate,
        dueDate,
        applyToAllApartments,
        type: 'OTHER'
      };

      await onSave(data);
      onClose();
      // Reset form
      setAmountInput('');
      setNotes('');
      setDueDate('');
      setApplyToAllApartments(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Có lỗi xảy ra khi thêm hóa đơn');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    const periods = generateBillingPeriods();
    setBillingPeriod(periods[0]?.value || '');
    setAmountInput('');
    setNotes('');
    setFromDate('');
    setDueDate('');
    setApplyToAllApartments(false);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-screen overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Thêm hóa đơn khác
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Kỳ hóa đơn
            </label>
            <select
              value={billingPeriod}
              onChange={(e) => setBillingPeriod(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              {billingPeriods.map((period) => (
                <option key={period.value} value={period.value}>
                  {period.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Số tiền
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={amountInput}
              onChange={(e) => setAmountInput(formatVndInput(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Số tiền"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ghi chú
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nội dung thu phí"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hạn thanh toán từ ngày
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Đến ngày
              </label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="applyToAll"
                checked={applyToAllApartments}
                onChange={(e) => setApplyToAllApartments(e.target.checked)}
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
              />
              <label htmlFor="applyToAll" className="ml-2 block text-sm text-gray-700">
                <span className="bg-green-600 text-white px-2 py-1 rounded text-xs mr-2">Thêm</span>
                hóa đơn này cho <span className="text-red-600 font-medium">tất cả</span> các căn hộ.
              </label>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Hủy
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
            >
              {loading ? 'Đang lưu...' : 'Lưu'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddOtherServiceFeeModal; 